
<script setup lang="ts">
import { ref, watch, nextTick, onUnmounted } from 'vue'
import Icon from '@/components/shared/Icon.vue'
import { lockBodyScroll, unlockBodyScroll } from '@/lib/bodyScrollLock'
const props = defineProps<{
  title: string
  show: boolean
  size?: 'default' | 'large' | 'small'
}>()

const emit = defineEmits<{
  close: []
}>()

const modalRef = ref<HTMLElement | null>(null)
let lastFocusedElement: HTMLElement | null = null

let mouseDownOnBackdrop = false;
function handleBackdropMouseDown(e: MouseEvent) {
  mouseDownOnBackdrop = e.target === e.currentTarget;
}
function handleBackdropClick(e: MouseEvent) {
  // Only close if mouse was pressed and released on the backdrop
  if (mouseDownOnBackdrop && e.target === e.currentTarget) {
    emit('close');
  }
  mouseDownOnBackdrop = false;
}

function trapFocus(e: KeyboardEvent) {
  if (!props.show) return;
  if (e.key === 'Tab') {
    const modal = modalRef.value;
    if (!modal) return;
    const focusable = modal.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  } else if (e.key === 'Escape') {
    emit('close');
  }
}

watch(() => props.show, async (show) => {
  if (show) {
    lockBodyScroll()
    lastFocusedElement = document.activeElement as HTMLElement
    await nextTick()
    // Focus the first focusable element or modal itself
    const modal = modalRef.value
    if (modal) {
      const focusable = modal.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
      )
      if (focusable.length) {
        focusable[0].focus()
      } else {
        modal.focus()
      }
    }
    document.addEventListener('keydown', trapFocus)
  } else {
    unlockBodyScroll()
    document.removeEventListener('keydown', trapFocus)
    if (lastFocusedElement) {
      lastFocusedElement.focus()
      lastFocusedElement = null
    }
  }
})

onUnmounted(() => {
  document.removeEventListener('keydown', trapFocus)
  // If the component is destroyed while open (e.g. route navigation), release the lock
  if (props.show) unlockBodyScroll()
})
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="show" class="modal-backdrop" @mousedown="handleBackdropMouseDown" @click="handleBackdropClick">
        <div
          :class="['modal', `modal-${size || 'default'}`]"
          role="dialog"
          aria-modal="true"
          tabindex="-1"
          ref="modalRef"
        >
          <div class="modal-header">
            <h2 class="modal-title">{{ title }}</h2>
            <button class="modal-close" @click="$emit('close')" aria-label="Close">
              <Icon name="close" size="md" />
            </button>
          </div>
          <div class="modal-body">
            <slot />
          </div>
          <div v-if="$slots.footer" class="modal-footer">
            <slot name="footer" />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal {
  background: var(--color-surface);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-xl);
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal-default {
  max-width: 800px;
}

.modal-large {
  max-width: 800px;
}

.modal-small {
  max-width: 400px;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--gray-200);
}

.modal-title {
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0;
  color: var(--gray-900);
}

.modal-close {
  background: none;
  border: none;
  padding: 0.25rem;
  cursor: pointer;
  height: 40px;
  width: 40px;
  color: var(--gray-400);
  border-radius: var(--radius-sm);
  transition: var(--transition-base);
}

.modal-close:hover {
  color: var(--gray-600);
  background: var(--gray-100);
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--gray-200);
  background: var(--gray-50);
  border-radius: 0 0 var(--radius-lg) var(--radius-lg);
}

/* Transitions */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}

.modal-enter-active .modal,
.modal-leave-active .modal {
  transition: transform 0.2s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal,
.modal-leave-to .modal {
  transform: scale(0.95) translateY(-10px);
}
</style>
